import { useCallback, useEffect, useRef, useState } from 'react';
import { api, resolveMediaUrl } from '../api/client';
import type { OrderChatMessage, OrderChatThread } from '../types';
import { buildOrderChatView, chatBubbleTime, draftOverLimit, type OrderChatRole } from './orderChatView';

/* ============================================================
   33f30c97 (Artem 2026-08-28 §3): order-scoped chat panel for
   the customer order detail and the provider queue card. Loads
   one page, marks the thread read on open, sends messages.
   Posting gate + lock copy come from the backend thread view —
   the panel never second-guesses order status.
   fa58b0eb: ≤3 photo attachments per message — staged via the
   party-scoped attachment endpoint, rendered as thumbnails in
   the same role-flip bubble layout.
   ============================================================ */

const MAX_BODY_LENGTH = 2000;
const MAX_PHOTOS = 3;
const PHOTO_ACCEPT = 'image/jpeg,image/png,image/webp';

export function OrderChatPanel({ orderId, role }: { orderId: string; role: OrderChatRole }) {
  const [thread, setThread] = useState<OrderChatThread | null>(null);
  const [draft, setDraft] = useState('');
  const [photos, setPhotos] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const load = useCallback(async () => {
    try {
      setError(null);
      const t = await api.getOrderChat(orderId);
      setThread(t);
      // Opening the thread with unread counterpart messages marks it read.
      if (t.unreadCount > 0) {
        await api.markOrderChatRead(orderId);
        setThread({ ...t, unreadCount: 0 });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить чат');
    }
  }, [orderId]);

  useEffect(() => { void load(); }, [load]);

  // Keep the newest message in view when the page grows.
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'nearest' });
  }, [thread?.messages.length]);

  const send = useCallback(async () => {
    const body = draft.trim();
    const staged = photos;
    if ((body.length === 0 && staged.length === 0) || body.length > MAX_BODY_LENGTH || busy) return;
    setBusy(true);
    try {
      setError(null);
      const sent: OrderChatMessage = await api.sendOrderChatMessage(orderId, body, staged);
      setDraft('');
      setPhotos([]);
      // Merge optimistically; the next open re-reads the server truth.
      setThread((t) => t === null ? t : {
        ...t,
        messages: [...t.messages, sent],
        hasMore: t.hasMore,
        unreadCount: 0,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось отправить');
    } finally {
      setBusy(false);
    }
  }, [busy, draft, orderId, photos]);

  const attachFiles = useCallback(async (files: FileList | null) => {
    if (files === null || files.length === 0 || uploading) return;
    const room = MAX_PHOTOS - photos.length;
    if (room <= 0) return;
    setUploading(true);
    try {
      setError(null);
      const accepted = Array.from(files).slice(0, room);
      const urls: string[] = [];
      for (const file of accepted) {
        const uploaded = await api.uploadOrderChatPhoto(orderId, file);
        urls.push(uploaded.url);
      }
      setPhotos((p) => [...p, ...urls].slice(0, MAX_PHOTOS));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить фото');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [orderId, photos.length, uploading]);

  const loadMore = useCallback(async () => {
    if (thread === null || thread.messages.length === 0) return;
    const oldest = thread.messages[0].sentAtUtc;
    try {
      const older = await api.getOrderChat(orderId, oldest);
      setThread((t) => t === null ? t : {
        ...t,
        messages: [...older.messages, ...t.messages],
        hasMore: older.hasMore,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить историю');
    }
  }, [orderId, thread]);

  const view = buildOrderChatView({ thread, role, draft, photoCount: photos.length });

  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
        <h4 style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-semibold)' }}>Чат по заказу</h4>
        {view.unreadLabel && (
          <span className="badge badge-warning">{view.unreadLabel}</span>
        )}
      </div>

      {error && (
        <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-danger)' }}>{error}</p>
      )}

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-sm)',
          maxHeight: 320,
          overflowY: 'auto',
          padding: 'var(--space-xs) 0',
        }}
      >
        {view.showLoadMore && (
          <button
            className="btn btn-tertiary"
            style={{ height: 32, padding: '4px 14px', alignSelf: 'center' }}
            onClick={() => { void loadMore(); }}
          >
            Загрузить ещё
          </button>
        )}

        {view.emptyCopy && (
          <p style={{ fontSize: 'var(--fs-body)', color: 'var(--color-text-secondary)', textAlign: 'center' }}>
            {view.emptyCopy}
          </p>
        )}

        {view.bubbles.map((b) => (
          <div
            key={b.id}
            style={{
              alignSelf: b.side === 'mine' ? 'flex-end' : 'flex-start',
              maxWidth: '80%',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            {b.side === 'theirs' && (
              <span style={{ fontSize: 'var(--fs-overline)', color: 'var(--color-text-tertiary)' }}>
                {b.senderLabel}
              </span>
            )}
            <div
              className={b.side === 'mine' ? 'chat-bubble-mine' : 'chat-bubble-theirs'}
              style={{
                borderRadius: 16,
                padding: '8px 14px',
                fontSize: 'var(--fs-body)',
                background: b.side === 'mine'
                  ? 'var(--color-accent-soft, #FFF1DE)'
                  : 'var(--color-bg, #F2F2F7)',
                color: 'var(--color-text-primary, #1C1C1E)',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {b.photoUrls.length > 0 && (
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 6,
                    marginBottom: b.body.length > 0 ? 6 : 0,
                  }}
                >
                  {b.photoUrls.map((url) => (
                    <img
                      key={url}
                      src={resolveMediaUrl(url)}
                      alt="Фото к сообщению"
                      loading="lazy"
                      style={{
                        width: b.photoUrls.length === 1 ? 180 : 96,
                        height: b.photoUrls.length === 1 ? 135 : 96,
                        objectFit: 'cover',
                        borderRadius: 12,
                        border: '1px solid var(--color-border, rgba(0,0,0,0.08))',
                        display: 'block',
                      }}
                    />
                  ))}
                </div>
              )}
              {b.body}
            </div>
            <span style={{ fontSize: 'var(--fs-overline)', color: 'var(--color-text-tertiary)' }}>
              {chatBubbleTime(b.sentAtIso)}
            </span>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {view.lockReason ? (
        <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
          {view.lockReason}
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)' }}>
          {photos.length > 0 && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              {photos.map((url) => (
                <div key={url} style={{ position: 'relative' }}>
                  <img
                    src={resolveMediaUrl(url)}
                    alt="Фото к сообщению"
                    style={{
                      width: 56,
                      height: 56,
                      objectFit: 'cover',
                      borderRadius: 10,
                      border: '1px solid var(--color-border, rgba(0,0,0,0.08))',
                      display: 'block',
                    }}
                  />
                  <button
                    type="button"
                    aria-label="Убрать фото"
                    onClick={() => setPhotos((p) => p.filter((u) => u !== url))}
                    style={{
                      position: 'absolute',
                      top: -6,
                      right: -6,
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      border: 'none',
                      background: 'var(--color-text-primary, #1C1C1E)',
                      color: '#fff',
                      fontSize: 12,
                      lineHeight: 1,
                      cursor: 'pointer',
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', gap: 'var(--space-sm)', alignItems: 'flex-end' }}>
            <input
              ref={fileInputRef}
              type="file"
              accept={PHOTO_ACCEPT}
              multiple
              style={{ display: 'none' }}
              onChange={(e) => { void attachFiles(e.target.files); }}
            />
            <button
              type="button"
              className="btn btn-tertiary"
              style={{ height: 40, padding: '8px 12px' }}
              title="Прикрепить фото (до 3)"
              disabled={photos.length >= MAX_PHOTOS || uploading || busy}
              onClick={() => fileInputRef.current?.click()}
            >
              {uploading ? '…' : '📷'}
            </button>
            <textarea
              className="input"
              style={{ flex: 1, minHeight: 40, resize: 'vertical' }}
              placeholder="Сообщение…"
              value={draft}
              maxLength={MAX_BODY_LENGTH + 1}
              rows={2}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void send();
                }
              }}
            />
            <button
              className="btn btn-primary"
              style={{ height: 40, padding: '8px 20px' }}
              disabled={!view.canSend || busy || uploading}
              onClick={() => { void send(); }}
            >
              {busy ? '…' : 'Отправить'}
            </button>
          </div>
        </div>
      )}
      {draftOverLimit(draft) && (
        <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-danger)' }}>
          Сообщение длиннее {MAX_BODY_LENGTH} символов
        </p>
      )}
    </div>
  );
}
